import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Search,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  ShieldQuestion,
  MoreHorizontal,
  Trash2,
  RefreshCw,
  Eye,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronRight,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

// ============================================
// Types
// ============================================

interface ControlScore {
  category: string
  name: string
  score: number
  notes?: string
  evidence?: string
}

interface Assessment {
  id: string
  clientName: string
  assessmentDate: string
  overallScore: number
  readinessLevel: string
  categories: Record<string, number>
  controls: ControlScore[]
  gaps: Array<{ category: string; score: number; recommendation: string }> | null
  insurerRequirements: Record<string, unknown> | null
  policyDetails: Record<string, unknown> | null
  nextReviewDate: string | null
  createdAt: string
}

interface AssessmentStats {
  total: number
  averageScore: number
  readinessDistribution: Record<string, number>
  commonGaps: Array<{ category: string; averageScore: number; assessmentCount: number }>
  categoryAverages: Record<string, number>
}

interface ControlTemplate {
  category: string
  name: string
  description: string
  weight: number
}

// ============================================
// Helpers
// ============================================

const categoryLabels: Record<string, string> = {
  mfa: 'Multi-Factor Auth',
  endpoint_protection: 'Endpoint Protection',
  backup: 'Backup & Recovery',
  email_security: 'Email Security',
  network_security: 'Network Security',
  access_control: 'Access Control',
  incident_response: 'Incident Response',
  training: 'Security Training',
  encryption: 'Encryption',
  patch_management: 'Patch Management',
  vulnerability_management: 'Vulnerability Mgmt',
  logging: 'Logging & Monitoring',
}

function readinessLevelBadge(level: string) {
  switch (level) {
    case 'excellent':
      return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Excellent</Badge>
    case 'ready':
      return <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">Ready</Badge>
    case 'partial':
      return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Partial</Badge>
    case 'not_ready':
      return <Badge variant="destructive">Not Ready</Badge>
    default:
      return <Badge variant="secondary">{level}</Badge>
  }
}

function readinessIcon(level: string) {
  switch (level) {
    case 'excellent': return <ShieldCheck className="h-5 w-5 text-green-500" />
    case 'ready': return <ShieldCheck className="h-5 w-5 text-blue-500" />
    case 'partial': return <ShieldAlert className="h-5 w-5 text-yellow-500" />
    case 'not_ready': return <ShieldX className="h-5 w-5 text-red-500" />
    default: return <ShieldQuestion className="h-5 w-5 text-muted-foreground" />
  }
}

function scoreColor(score: number): string {
  if (score >= 90) return 'text-green-500'
  if (score >= 70) return 'text-blue-500'
  if (score >= 40) return 'text-yellow-500'
  return 'text-red-500'
}

function scoreBgColor(score: number): string {
  if (score >= 90) return 'bg-green-500'
  if (score >= 70) return 'bg-blue-500'
  if (score >= 40) return 'bg-yellow-500'
  return 'bg-red-500'
}

// ============================================
// Score Gauge Component
// ============================================

function ScoreGauge({ score, size = 'md' }: { score: number; size?: 'sm' | 'md' | 'lg' }) {
  const radius = size === 'lg' ? 50 : size === 'md' ? 40 : 30
  const strokeWidth = size === 'lg' ? 8 : size === 'md' ? 6 : 4
  const circumference = 2 * Math.PI * radius
  const progress = (score / 100) * circumference
  const svgSize = (radius + strokeWidth) * 2
  const fontSize = size === 'lg' ? 'text-2xl' : size === 'md' ? 'text-xl' : 'text-sm'

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={svgSize} height={svgSize} className="-rotate-90">
        <circle
          cx={radius + strokeWidth}
          cy={radius + strokeWidth}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-muted/30"
        />
        <circle
          cx={radius + strokeWidth}
          cy={radius + strokeWidth}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          strokeLinecap="round"
          className={scoreColor(score)}
        />
      </svg>
      <span className={`absolute font-bold ${fontSize} ${scoreColor(score)}`}>{score}</span>
    </div>
  )
}

// ============================================
// Radar Chart Component (CSS-based)
// ============================================

function CategoryRadar({ categories }: { categories: Record<string, number> }) {
  const entries = Object.entries(categories)
  if (entries.length === 0) return null

  return (
    <div className="space-y-2">
      {entries.map(([category, score]) => (
        <div key={category} className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">{categoryLabels[category] || category}</span>
            <span className={`font-medium ${scoreColor(score)}`}>{score}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${scoreBgColor(score)}`}
              style={{ width: `${score}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

// ============================================
// Create Assessment Dialog
// ============================================

function CreateAssessmentDialog() {
  const [open, setOpen] = useState(false)
  const [clientName, setClientName] = useState('')
  const [assessmentDate, setAssessmentDate] = useState(new Date().toISOString().split('T')[0])
  const [controlScores, setControlScores] = useState<Record<string, number>>({})
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Fetch control checklist
  const { data: checklistData } = useQuery({
    queryKey: ['cyber-insurance', 'controls'],
    queryFn: () => apiClient.get<{ categories: string[]; controls: ControlTemplate[] }>('/cyber-insurance/controls'),
    enabled: open,
  })

  const controls = checklistData?.data?.controls || []
  const categories = checklistData?.data?.categories || []

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/cyber-insurance/assessments', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cyber-insurance'] })
      toast({ title: 'Assessment created', description: 'Insurance readiness assessment has been saved' })
      setOpen(false)
      setClientName('')
      setControlScores({})
    },
    onError: () => toast({ title: 'Error', description: 'Failed to create assessment', variant: 'destructive' }),
  })

  function toggleCategory(cat: string) {
    const next = new Set(expandedCategories)
    if (next.has(cat)) next.delete(cat)
    else next.add(cat)
    setExpandedCategories(next)
  }

  function handleSubmit() {
    const controlsList = controls.map((c: ControlTemplate) => ({
      category: c.category,
      name: c.name,
      score: controlScores[`${c.category}:${c.name}`] ?? 0,
    }))

    createMutation.mutate({
      clientName,
      assessmentDate,
      controls: controlsList,
    })
  }

  // Group controls by category
  const controlsByCategory = useMemo(() => {
    const grouped: Record<string, ControlTemplate[]> = {}
    for (const c of controls) {
      if (!grouped[c.category]) grouped[c.category] = []
      grouped[c.category].push(c)
    }
    return grouped
  }, [controls])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button><Plus className="mr-2 h-4 w-4" /> New Assessment</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle>Insurance Readiness Assessment</DialogTitle>
          <DialogDescription>Score each control from 0-100 to calculate readiness.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4 overflow-y-auto max-h-[55vh]">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Client Name *</Label>
              <Input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Client Pty Ltd" />
            </div>
            <div className="space-y-2">
              <Label>Assessment Date</Label>
              <Input type="date" value={assessmentDate} onChange={(e) => setAssessmentDate(e.target.value)} />
            </div>
          </div>

          <div className="border rounded-lg divide-y">
            {categories.map((cat: string) => {
              const catControls = controlsByCategory[cat] || []
              const isExpanded = expandedCategories.has(cat)
              const catScores = catControls.map((c: ControlTemplate) => controlScores[`${c.category}:${c.name}`] ?? 0)
              const avgScore = catScores.length > 0 ? Math.round(catScores.reduce((a: number, b: number) => a + b, 0) / catScores.length) : 0

              return (
                <div key={cat}>
                  <button
                    type="button"
                    className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
                    onClick={() => toggleCategory(cat)}
                  >
                    <div className="flex items-center gap-2">
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      <span className="text-sm font-medium">{categoryLabels[cat] || cat}</span>
                      <Badge variant="secondary" className="text-xs">{catControls.length}</Badge>
                    </div>
                    <span className={`text-sm font-medium ${scoreColor(avgScore)}`}>{avgScore}%</span>
                  </button>
                  {isExpanded && (
                    <div className="px-3 pb-3 space-y-3">
                      {catControls.map((control: ControlTemplate) => {
                        const key = `${control.category}:${control.name}`
                        return (
                          <div key={key} className="flex items-center gap-3">
                            <div className="flex-1">
                              <p className="text-sm">{control.name}</p>
                              <p className="text-xs text-muted-foreground">{control.description}</p>
                            </div>
                            <Input
                              type="number"
                              className="w-20 text-center"
                              min={0}
                              max={100}
                              value={controlScores[key] ?? ''}
                              onChange={(e) => setControlScores(prev => ({ ...prev, [key]: parseInt(e.target.value) || 0 }))}
                              placeholder="0"
                            />
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={!clientName || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create Assessment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Assessment Detail Dialog
// ============================================

function AssessmentDetailDialog({ assessment, onClose }: { assessment: Assessment; onClose: () => void }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {readinessIcon(assessment.readinessLevel)}
            {assessment.clientName}
          </DialogTitle>
          <DialogDescription>
            Assessment from {new Date(assessment.assessmentDate).toLocaleDateString('en-AU')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 py-4 overflow-y-auto max-h-[55vh]">
          {/* Score Summary */}
          <div className="flex items-center gap-6">
            <ScoreGauge score={assessment.overallScore} size="lg" />
            <div>
              <div className="mb-1">{readinessLevelBadge(assessment.readinessLevel)}</div>
              <p className="text-sm text-muted-foreground">Overall Readiness Score</p>
              {assessment.nextReviewDate && (
                <p className="text-xs text-muted-foreground mt-1">
                  Next review: {new Date(assessment.nextReviewDate).toLocaleDateString('en-AU')}
                </p>
              )}
            </div>
          </div>

          {/* Category Scores */}
          <div>
            <h3 className="text-sm font-medium mb-3">Category Scores</h3>
            <CategoryRadar categories={assessment.categories || {}} />
          </div>

          {/* Gap Analysis */}
          {assessment.gaps && assessment.gaps.length > 0 && (
            <div>
              <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-500" />
                Gap Analysis
              </h3>
              <div className="space-y-2">
                {assessment.gaps.map((gap, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm border rounded-lg p-3">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium">{categoryLabels[gap.category] || gap.category}</p>
                      <p className="text-muted-foreground text-xs">{gap.recommendation}</p>
                    </div>
                    <span className={`ml-auto font-mono text-sm ${scoreColor(gap.score)}`}>{gap.score}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Controls Detail */}
          {assessment.controls && assessment.controls.length > 0 && (
            <div>
              <h3 className="text-sm font-medium mb-3">Control Scores</h3>
              <div className="space-y-1.5">
                {assessment.controls.map((control: ControlScore, i: number) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    {control.score >= 70 ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
                    ) : control.score >= 40 ? (
                      <AlertTriangle className="h-3.5 w-3.5 text-yellow-500 shrink-0" />
                    ) : (
                      <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                    )}
                    <span className="text-muted-foreground flex-1">{control.name}</span>
                    <span className={`font-mono ${scoreColor(control.score)}`}>{control.score}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Donut Chart Component (CSS-based)
// ============================================

function ReadinessDonut({ distribution }: { distribution: Record<string, number> }) {
  const total = Object.values(distribution).reduce((a, b) => a + b, 0)
  if (total === 0) {
    return (
      <div className="flex items-center justify-center h-40">
        <p className="text-sm text-muted-foreground">No assessments yet</p>
      </div>
    )
  }

  const colors: Record<string, string> = {
    excellent: '#22c55e',
    ready: '#3b82f6',
    partial: '#eab308',
    not_ready: '#ef4444',
  }

  const labels: Record<string, string> = {
    excellent: 'Excellent',
    ready: 'Ready',
    partial: 'Partial',
    not_ready: 'Not Ready',
  }

  // Build conic gradient
  let cumulative = 0
  const segments: string[] = []
  for (const [level, count] of Object.entries(distribution)) {
    const pct = (count / total) * 100
    const color = colors[level] || '#6b7280'
    segments.push(`${color} ${cumulative}% ${cumulative + pct}%`)
    cumulative += pct
  }

  return (
    <div className="flex items-center gap-6">
      <div
        className="w-32 h-32 rounded-full relative"
        style={{
          background: `conic-gradient(${segments.join(', ')})`,
        }}
      >
        <div className="absolute inset-4 rounded-full bg-background flex items-center justify-center">
          <span className="text-lg font-bold">{total}</span>
        </div>
      </div>
      <div className="space-y-2">
        {Object.entries(distribution).map(([level, count]) => (
          <div key={level} className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colors[level] || '#6b7280' }} />
            <span className="text-muted-foreground">{labels[level] || level}</span>
            <span className="font-medium ml-auto">{count}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================================
// Main Page
// ============================================

export function CyberInsurancePage() {
  const [search, setSearch] = useState('')
  const [readinessFilter, setReadinessFilter] = useState<string>('all')
  const [selectedAssessment, setSelectedAssessment] = useState<Assessment | null>(null)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Fetch assessments
  const { data: assessmentsData, isLoading } = useQuery({
    queryKey: ['cyber-insurance', 'assessments', readinessFilter],
    queryFn: () => apiClient.get<{ data: Assessment[]; meta: unknown }>('/cyber-insurance/assessments', {
      ...(readinessFilter !== 'all' && { readinessLevel: readinessFilter }),
      limit: 100,
    }),
  })

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['cyber-insurance', 'stats'],
    queryFn: () => apiClient.get<AssessmentStats>('/cyber-insurance/stats'),
  })

  const recalculateMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/cyber-insurance/assessments/${id}/recalculate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cyber-insurance'] })
      toast({ title: 'Recalculated', description: 'Assessment scores have been updated' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to recalculate', variant: 'destructive' }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/cyber-insurance/assessments/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cyber-insurance'] })
      toast({ title: 'Deleted', description: 'Assessment has been removed' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to delete assessment', variant: 'destructive' }),
  })

  const rawAssessments = assessmentsData?.data
  const assessments: Assessment[] = Array.isArray(rawAssessments)
    ? rawAssessments
    : (rawAssessments as { data: Assessment[] } | undefined)?.data || []
  const stats = statsData?.data || null

  const filtered = Array.isArray(assessments)
    ? assessments.filter((a) => a.clientName.toLowerCase().includes(search.toLowerCase()))
    : []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Cyber Insurance Readiness</h1>
          <p className="text-muted-foreground">Assess and track insurance readiness for your clients</p>
        </div>
        <CreateAssessmentDialog />
      </div>

      {/* Stats Row */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Total Assessments</p>
            </div>
            <div className="text-2xl font-bold mt-1">{stats?.total ?? 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-blue-500" />
              <p className="text-xs text-muted-foreground">Average Score</p>
            </div>
            <div className={`text-2xl font-bold mt-1 ${scoreColor(stats?.averageScore ?? 0)}`}>
              {stats?.averageScore ?? 0}/100
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <p className="text-xs text-muted-foreground">Ready / Excellent</p>
            </div>
            <div className="text-2xl font-bold text-green-500 mt-1">
              {(stats?.readinessDistribution?.ready ?? 0) + (stats?.readinessDistribution?.excellent ?? 0)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <p className="text-xs text-muted-foreground">Not Ready</p>
            </div>
            <div className="text-2xl font-bold text-red-500 mt-1">
              {stats?.readinessDistribution?.not_ready ?? 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="assessments">
        <TabsList>
          <TabsTrigger value="assessments">Assessments</TabsTrigger>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="gaps">Gap Analysis</TabsTrigger>
        </TabsList>

        {/* Assessments Tab */}
        <TabsContent value="assessments" className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search clients..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select value={readinessFilter} onValueChange={setReadinessFilter}>
              <SelectTrigger className="w-[170px]"><SelectValue placeholder="Readiness" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="excellent">Excellent</SelectItem>
                <SelectItem value="ready">Ready</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="not_ready">Not Ready</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Assessment Cards */}
          {isLoading ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <Card key={i}>
                  <CardHeader><Skeleton className="h-5 w-32" /></CardHeader>
                  <CardContent><Skeleton className="h-20 w-full" /></CardContent>
                </Card>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <ShieldQuestion className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
                <p className="text-lg font-medium text-muted-foreground">No assessments found</p>
                <p className="text-sm text-muted-foreground">Start by creating an assessment for a client</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((assessment) => (
                <Card key={assessment.id} className="cursor-pointer hover:border-primary/30 transition-colors" onClick={() => setSelectedAssessment(assessment)}>
                  <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
                    <div className="space-y-1">
                      <CardTitle className="text-base font-medium">{assessment.clientName}</CardTitle>
                      <p className="text-xs text-muted-foreground">
                        {new Date(assessment.assessmentDate).toLocaleDateString('en-AU')}
                      </p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setSelectedAssessment(assessment); }}>
                          <Eye className="mr-2 h-4 w-4" /> View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); recalculateMutation.mutate(assessment.id); }}>
                          <RefreshCw className="mr-2 h-4 w-4" /> Recalculate
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(assessment.id); }}>
                          <Trash2 className="mr-2 h-4 w-4" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 mb-4">
                      <ScoreGauge score={assessment.overallScore} size="sm" />
                      <div>
                        {readinessLevelBadge(assessment.readinessLevel)}
                        <p className="text-xs text-muted-foreground mt-1">Readiness Score</p>
                      </div>
                    </div>

                    {/* Top 4 category scores */}
                    {assessment.categories && (
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                        {Object.entries(assessment.categories).slice(0, 4).map(([cat, score]) => (
                          <div key={cat} className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground truncate">{categoryLabels[cat] || cat}</span>
                            <span className={`font-medium ${scoreColor(score as number)}`}>{score as number}%</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Gaps indicator */}
                    {assessment.gaps && assessment.gaps.length > 0 && (
                      <div className="mt-3 flex items-center gap-1.5 text-xs text-yellow-500">
                        <AlertTriangle className="h-3 w-3" />
                        {assessment.gaps.length} gap{assessment.gaps.length !== 1 ? 's' : ''} identified
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Readiness Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <ReadinessDonut distribution={stats?.readinessDistribution || {}} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Category Averages (All Clients)</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.categoryAverages && Object.keys(stats.categoryAverages).length > 0 ? (
                  <CategoryRadar categories={stats.categoryAverages} />
                ) : (
                  <p className="text-sm text-muted-foreground">No data available</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Gaps Tab */}
        <TabsContent value="gaps" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-500" />
                Common Gaps Across All Clients
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.commonGaps && stats.commonGaps.length > 0 ? (
                <div className="space-y-3">
                  {stats.commonGaps.map((gap, i) => (
                    <div key={i} className="flex items-center gap-4 border rounded-lg p-4">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{categoryLabels[gap.category] || gap.category}</p>
                        <p className="text-xs text-muted-foreground">
                          Average score across {gap.assessmentCount} assessment{gap.assessmentCount !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className={`text-xl font-bold ${scoreColor(gap.averageScore)}`}>{gap.averageScore}%</div>
                        <div className="h-2 w-24 bg-muted rounded-full overflow-hidden mt-1">
                          <div
                            className={`h-full rounded-full ${scoreBgColor(gap.averageScore)}`}
                            style={{ width: `${gap.averageScore}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  <CheckCircle2 className="h-8 w-8 text-green-500 mb-2" />
                  <p className="text-sm text-muted-foreground">No common gaps identified</p>
                  <p className="text-xs text-muted-foreground">All categories are averaging above 60%</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Per-Category Breakdown */}
          {stats?.categoryAverages && Object.keys(stats.categoryAverages).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Full Category Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {Object.entries(stats.categoryAverages)
                    .sort(([, a], [, b]) => (a as number) - (b as number))
                    .map(([cat, score]) => (
                      <div key={cat} className="flex items-center gap-3 border rounded-lg p-3">
                        {(score as number) >= 70 ? (
                          <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                        ) : (score as number) >= 40 ? (
                          <AlertTriangle className="h-5 w-5 text-yellow-500 shrink-0" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{categoryLabels[cat] || cat}</p>
                          <div className="h-1.5 bg-muted rounded-full overflow-hidden mt-1">
                            <div
                              className={`h-full rounded-full ${scoreBgColor(score as number)}`}
                              style={{ width: `${score}%` }}
                            />
                          </div>
                        </div>
                        <span className={`font-mono text-sm font-medium ${scoreColor(score as number)}`}>
                          {score as number}%
                        </span>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Assessment Detail Dialog */}
      {selectedAssessment && (
        <AssessmentDetailDialog
          assessment={selectedAssessment}
          onClose={() => setSelectedAssessment(null)}
        />
      )}
    </div>
  )
}
